import {
  Avatar, Button, type ColumnDef, DataTable, InlineMessage, OptionSelect, Spinner, Stack, Text,
} from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { monthRange, shortDate, hoursText } from './model';
import type { AvatarOf } from './WorkPage';
import { addMember, fetchTeam, removeMember, type TeamMember, type WorkBootstrap, type WorkProject } from './workApi';

/** A project's team: who sees it, how they joined, and their assigned hours. */
export function WorkTeam({
  project, boot, avatarOf, onChanged, say,
}: {
  project: WorkProject;
  boot: WorkBootstrap;
  avatarOf: AvatarOf;
  onChanged: () => void;
  say: (text: string, tone?: 'info' | 'error') => void;
}): JSX.Element {
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [adding, setAdding] = useState('');
  const [busy, setBusy] = useState(false);
  const manage = boot.me === null || boot.me.access === 'all';

  useEffect(() => {
    let live = true;
    fetchTeam(authenticatedFetch, project.id)
      .then((m) => live && setMembers(m))
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : 'Could not load the team.'));
    return () => {
      live = false;
    };
  }, [project.id, reload]);

  const run = async (work: () => Promise<unknown>, text: string) => {
    setBusy(true);
    try {
      await work();
      say(text);
      setReload((n) => n + 1);
      onChanged();
    } catch (e) {
      say(e instanceof Error ? e.message : 'Could not change the team.', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <InlineMessage tone="error" role="alert">{error}</InlineMessage>;
  if (!members) return <div className="flex justify-center py-10"><Spinner label="Loading the team" /></div>;

  const active = members.filter((m) => !m.removed_at);
  const removed = members.filter((m) => m.removed_at);
  const candidates = boot.people.filter((p) => !active.some((m) => m.app_user_id === p.id));

  const columns: ColumnDef<TeamMember>[] = [
    {
      key: 'name',
      label: 'Member',
      render: (m) => {
        const a = avatarOf(m.app_user_id);
        return (
          <span className="flex items-center gap-2.5">
            <Avatar size="md" name={m.name} {...(a?.src ? { src: a.src } : {})} decorative />
            <span className="flex min-w-0 flex-col">
              <span className="font-emphasis text-[var(--fg-strong)]">{m.name}</span>
              <span className="truncate text-xs text-[var(--fg-muted)]">{m.email}</span>
            </span>
          </span>
        );
      },
    },
    {
      key: 'project_access',
      label: 'Access',
      render: (m) => (m.project_access === 'all'
        ? <span className="text-[var(--fg-muted)]">All projects</span>
        : <span className="font-emphasis text-[var(--fg-strong)]">This project only</span>),
    },
    {
      key: 'source',
      label: 'Joined',
      render: (m) => <>{m.source === 'assignment' ? 'Resource assignment' : 'Added manually'} <span className="text-[var(--fg-muted)]">· {shortDate(m.added_at)}</span></>,
    },
    {
      key: 'assigned_hours',
      label: 'Assigned Hours',
      render: (m) => (m.assigned_hours
        ? <><span className="tabular-nums">{hoursText(m.assigned_hours)} h</span> <span className="text-[var(--fg-muted)]">· {monthRange(m.first_month, m.last_month)}</span></>
        : <span className="text-[var(--fg-faint)]">—</span>),
    },
  ];

  return (
    <Stack gap="lg" className="pt-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <Text as="h3" className="m-0 font-emphasis text-[var(--fg-strong)]">Project team</Text>
        {manage && candidates.length ? (
          <div className="flex items-center gap-2">
            <OptionSelect
              aria-label="Person to add"
              options={candidates.map((p) => ({ value: p.id, label: p.name }))}
              value={adding}
              placeholder="Choose a person"
              onValueChange={setAdding}
            />
            <Button
              variant="brand"
              disabled={!adding}
              loading={busy}
              onClick={() => {
                const name = boot.people.find((p) => p.id === adding)?.name ?? 'them';
                void run(() => addMember(authenticatedFetch, project.id, adding), `Added ${name} to the ${project.code} team`).then(() => setAdding(''));
              }}
            >
              Add member
            </Button>
          </div>
        ) : null}
      </div>

      <DataTable<TeamMember>
        columns={columns}
        rows={active}
        getRowId={(m) => m.app_user_id}
        {...(manage ? { rowActions: [{
          label: 'Remove',
          destructive: true,
          onSelect: (m) => void run(
            () => removeMember(authenticatedFetch, project.id, m.app_user_id),
            m.project_access === 'all'
              ? `Removed ${m.name}. They still see ${project.code} through all-project access.`
              : `Removed ${m.name}. They can no longer see ${project.name}.`,
          ),
        }] } : {})}
        rowActionsTrigger="chevron"
        empty={<Text tone="muted" size="sm">No one is on this team yet.</Text>}
      />

      {manage && removed.length ? (
        <Stack gap="sm">
          <Text as="h3" className="m-0 font-emphasis text-[var(--fg-strong)]">Removed</Text>
          <div className="flex flex-col rounded-[var(--radius-lg)] border border-[var(--border)]">
            {removed.map((m) => {
              const a = avatarOf(m.app_user_id);
              return (
                <div key={m.app_user_id} className="flex items-center gap-2.5 border-b border-[var(--border-subtle)] px-3 py-2 last:border-b-0">
                  <Avatar size="sm" name={m.name} {...(a?.src ? { src: a.src } : {})} decorative />
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--fg-strong)]">{m.name}</span>
                  <span className="text-xs text-[var(--fg-muted)]">Removed {shortDate(m.removed_at)}</span>
                  <Button variant="link" size="sm" disabled={busy} onClick={() => void run(() => addMember(authenticatedFetch, project.id, m.app_user_id), `${m.name} is back on the team`)}>
                    Add back
                  </Button>
                </div>
              );
            })}
          </div>
        </Stack>
      ) : null}
    </Stack>
  );
}
